import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  Send,
  X,
  Loader2,
  Bot,
  User,
  ChevronDown,
  FileText,
  FileWarning,
  Calculator,
  Scale,
  Sparkles,
  ExternalLink,
  Copy,
  Check,
  SlidersHorizontal,
  AlertTriangle,
  ArrowRight,
  Tags,
  ShieldAlert,
} from 'lucide-react';
import {
  intelligenceApi,
  ChatMessage,
  ChatResponse,
  Citation,
  ToolCall,
  PrepareClaimResult,
  OverrideProposal,
  MissingDocumentProposal,
  DiscrepancyProposal,
  TransactionProposal,
  AnomalyProposal,
} from '../services/intelligenceApi';
import { toast } from 'sonner';
import { formatCurrency } from '../utils/financialFormat';

// ---------------------------------------------------------------------------
// Prepare-claim proposal cards (Forensic Assistant "prep the claim")
// ---------------------------------------------------------------------------

/** Render a proposal `current_value` / `suggested_value`. Numbers render as
 * whole-dollar monospace (right-aligned by the caller); non-null strings pass
 * through; null renders the "value to enter" placeholder used for AI overrides
 * that intentionally withhold a dollar figure for the human to supply. */
function formatProposalValue(
  value: any | null,
  nullLabel: string,
): { text: string; isPlaceholder: boolean } {
  if (value === null || value === undefined || value === '') {
    return { text: nullLabel, isPlaceholder: true };
  }
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return {
      text: formatCurrency(value),
      isPlaceholder: false,
    };
  }
  // Number-shaped strings → treat as dollars for consistent presentation.
  if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.trim())) {
    const n = Number(value);
    if (!Number.isNaN(n)) {
      return {
        text: formatCurrency(n),
        isPlaceholder: false,
      };
    }
  }
  return { text: String(value), isPlaceholder: false };
}

// Token-based badge styles (theme-aware). Attention/danger severities map to
// the orange brand tint; neutral low/info to the elevated surface.
const ATTENTION_BADGE: React.CSSProperties = {
  backgroundColor: 'var(--color-needs-human-tint)',
  color: 'var(--color-needs-human-text)',
};
const NEUTRAL_BADGE: React.CSSProperties = {
  backgroundColor: 'var(--color-bg-elevated)',
  color: 'var(--color-text-secondary)',
};

const PRIORITY_BADGE: Record<string, React.CSSProperties> = {
  high: ATTENTION_BADGE,
  medium: ATTENTION_BADGE,
  low: NEUTRAL_BADGE,
};

const SEVERITY_BADGE: Record<string, React.CSSProperties> = {
  critical: ATTENTION_BADGE,
  high: ATTENTION_BADGE,
  warning: ATTENTION_BADGE,
  medium: ATTENTION_BADGE,
  info: NEUTRAL_BADGE,
  low: NEUTRAL_BADGE,
};

function badgeStyle(map: Record<string, React.CSSProperties>, key: string): React.CSSProperties {
  return map[key?.toLowerCase()] || NEUTRAL_BADGE;
}

interface CaseIntelligenceChatProps {
  reportId: string;
  reportName?: string;
  isOpen: boolean;
  onClose: () => void;
  /**
   * Audience scope. Omit (default) for the full attorney-facing toolset.
   * "accountant" narrows the backend toolset to forensic/calculation tools and
   * withholds attorney work-product / litigation-strategy (privilege scope).
   */
  toolScope?: string;
}

// Tool icons mapping
const TOOL_ICONS: Record<string, React.ElementType> = {
  'get_case_summary': FileText,
  'get_settlement_range': Scale,
  'run_sensitivity_analysis': Calculator,
  'run_monte_carlo': Calculator,
  'run_whatif_scenario': Calculator,
  'get_attorney_perspective': Scale,
  'get_adjuster_perspective': Scale,
  'get_forensic_perspective': FileText,
  'search_case_documents': FileText,
  'get_issue_status': FileText,
  'prepare_claim': Sparkles,
};

// Tool display names
const TOOL_NAMES: Record<string, string> = {
  'get_case_summary': 'Case Summary',
  'get_settlement_range': 'Settlement Range',
  'run_sensitivity_analysis': 'Sensitivity Analysis',
  'run_monte_carlo': 'Monte Carlo Simulation',
  'run_whatif_scenario': 'What-If Scenario',
  'get_attorney_perspective': 'Attorney Perspective',
  'get_adjuster_perspective': 'Adjuster Perspective',
  'get_forensic_perspective': 'Forensic Analysis',
  'search_case_documents': 'Document Search',
  'get_issue_status': 'Issue Status',
  'get_transaction_details': 'Transaction Details',
  'run_game_theory_analysis': 'Game Theory Analysis',
  'run_litigation_economics': 'Litigation Economics',
  'prepare_claim': 'Prep the Claim',
};

// Stable per-proposal key so we can track filed state across a list without
// relying on array index (cards can be filed individually or in bulk).
function missingDocKey(p: MissingDocumentProposal): string {
  return `missing_document:${p.doc_type}`;
}
function discrepancyKey(p: DiscrepancyProposal): string {
  return `discrepancy:${p.check_type}:${p.message}`;
}
function transactionKey(p: TransactionProposal): string {
  return `transaction:${p.transaction_id}`;
}
function anomalyKey(p: AnomalyProposal): string {
  return `anomaly:${p.anomaly_id}`;
}

/** Render a transaction amount as monospace whole dollars, negatives shown in
 * red accounting parentheses, null rendered as the em-dash zero placeholder per
 * the financial-data house style. Returns the text plus whether it should
 * render in the negative (red) color. */
function formatTxnAmount(amount: number | null): {
  text: string;
  isNegative: boolean;
} {
  if (amount === null || amount === undefined || Number.isNaN(amount)) {
    return { text: '—', isNegative: false };
  }
  const abs = Math.abs(amount).toLocaleString('en-US', {
    maximumFractionDigits: 0,
  });
  if (amount < 0) {
    return { text: `($${abs})`, isNegative: true };
  }
  return { text: `$${abs}`, isNegative: false };
}

interface ProposalCardsProps {
  result: PrepareClaimResult;
  reportId: string;
  navigate: ReturnType<typeof useNavigate>;
}

/**
 * Renders a `prepare_claim` tool result as three grouped sections of cards:
 * override proposals (deep-linked into the attested override modal), missing
 * documents, and discrepancies (filed as tracked issues). Privilege scope:
 * this lives inside the accountant-scoped Forensic Assistant — purely
 * forensic/data prep, no litigation strategy.
 */
function ProposalCards({ result, reportId, navigate }: ProposalCardsProps) {
  // Keys of proposals that have been successfully filed as issues.
  const [filedKeys, setFiledKeys] = useState<Set<string>>(new Set());
  // Keys currently in-flight (button shows a spinner / disabled).
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());
  // Keys of propose→apply cards (transaction / anomaly) that have settled to a
  // terminal "Applied"/"Resolved" state, and those currently in-flight.
  const [appliedKeys, setAppliedKeys] = useState<Set<string>>(new Set());
  const [applyingKeys, setApplyingKeys] = useState<Set<string>>(new Set());

  const overrides = result.override_proposals || [];
  const missing = result.missing_documents || [];
  const discrepancies = result.discrepancies || [];
  const transactions = result.transaction_proposals || [];
  const anomalies = result.anomaly_proposals || [];

  const hasAnything =
    overrides.length > 0 ||
    missing.length > 0 ||
    discrepancies.length > 0 ||
    transactions.length > 0 ||
    anomalies.length > 0;
  if (!hasAnything) return null;

  const markApplying = (key: string, on: boolean) =>
    setApplyingKeys((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  const markApplied = (key: string) =>
    setAppliedKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });

  const fileProposals = async (
    proposals: Array<MissingDocumentProposal | DiscrepancyProposal>,
    keys: string[],
  ) => {
    if (proposals.length === 0) return;
    setPendingKeys((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => next.add(k));
      return next;
    });
    try {
      const res = await intelligenceApi.fileIssues(reportId, proposals);
      const createdN = res.created?.length ?? 0;
      const dupN = res.duplicates?.length ?? 0;
      const errN = res.errors?.length ?? 0;

      // Mark every attempted card as filed: created OR already-tracked both
      // mean "it's now an issue", so the button should settle to "Filed".
      setFiledKeys((prev) => {
        const next = new Set(prev);
        keys.forEach((k) => next.add(k));
        return next;
      });

      const dupSuffix = dupN > 0 ? ` (${dupN} already tracked)` : '';
      if (createdN > 0 || dupN > 0) {
        toast.success(
          `Filed ${createdN} issue${createdN === 1 ? '' : 's'}${dupSuffix}`,
        );
      }
      if (errN > 0) {
        toast.error(
          `${errN} issue${errN === 1 ? '' : 's'} could not be filed`,
        );
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to file issues');
    } finally {
      setPendingKeys((prev) => {
        const next = new Set(prev);
        keys.forEach((k) => next.delete(k));
        return next;
      });
    }
  };

  // "File all" helpers — only file the not-yet-filed cards in each group.
  const unfiledMissing = missing.filter((p) => !filedKeys.has(missingDocKey(p)));
  const unfiledDiscrepancies = discrepancies.filter(
    (p) => !filedKeys.has(discrepancyKey(p)),
  );

  return (
    <div className="mt-3 space-y-4" data-testid="prepare-claim-proposals">
      {/* ---- Override proposals ---- */}
      {overrides.length > 0 && (
        <section data-testid="proposal-group-overrides">
          <div className="flex items-center gap-1.5 mb-2">
            <SlidersHorizontal className="w-3.5 h-3.5" style={{ color: 'var(--color-needs-human-text)' }} />
            <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-label)' }}>
              Override proposals
            </h4>
            <span className="text-xs" style={{ color: 'var(--color-text-dim)' }}>({overrides.length})</span>
          </div>
          <div className="space-y-2">
            {overrides.map((p, idx) => (
              <OverrideProposalCard
                key={`${p.engine_id}:${p.field_id}:${idx}`}
                proposal={p}
                onReview={() =>
                  navigate('/dashboard/accountant/review/' + reportId, {
                    state: {
                      overrideSuggestion: {
                        engineId: p.engine_id,
                        fieldId: p.field_id,
                        fieldLabel: p.field_label,
                        originalValue: p.current_value,
                        suggestedValue: p.suggested_value,
                        rationale: p.rationale,
                        origin: 'from_ai_suggestion',
                        promptedByAiAuditId: result.ai_audit_id,
                      },
                    },
                  })
                }
              />
            ))}
          </div>
        </section>
      )}

      {/* ---- Missing documents ---- */}
      {missing.length > 0 && (
        <section data-testid="proposal-group-missing">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <FileWarning className="w-3.5 h-3.5" style={{ color: 'var(--color-brand)' }} />
              <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-label)' }}>
                Missing documents
              </h4>
              <span className="text-xs" style={{ color: 'var(--color-text-dim)' }}>({missing.length})</span>
            </div>
            {unfiledMissing.length > 0 && (
              <button
                onClick={() =>
                  fileProposals(
                    unfiledMissing,
                    unfiledMissing.map(missingDocKey),
                  )
                }
                disabled={unfiledMissing.some((p) =>
                  pendingKeys.has(missingDocKey(p)),
                )}
                className="text-xs font-medium disabled:opacity-50"
                style={{ color: 'var(--color-needs-human-text)' }}
                data-testid="file-all-missing"
              >
                File all as issues
              </button>
            )}
          </div>
          <div className="space-y-2">
            {missing.map((p, idx) => {
              const key = missingDocKey(p);
              return (
                <MissingDocCard
                  key={`${p.doc_type}:${idx}`}
                  proposal={p}
                  filed={filedKeys.has(key)}
                  pending={pendingKeys.has(key)}
                  onFile={() => fileProposals([p], [key])}
                />
              );
            })}
          </div>
        </section>
      )}

      {/* ---- Discrepancies ---- */}
      {discrepancies.length > 0 && (
        <section data-testid="proposal-group-discrepancies">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" style={{ color: 'var(--color-needs-human-text)' }} />
              <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-label)' }}>
                Discrepancies
              </h4>
              <span className="text-xs" style={{ color: 'var(--color-text-dim)' }}>
                ({discrepancies.length})
              </span>
            </div>
            {unfiledDiscrepancies.length > 0 && (
              <button
                onClick={() =>
                  fileProposals(
                    unfiledDiscrepancies,
                    unfiledDiscrepancies.map(discrepancyKey),
                  )
                }
                disabled={unfiledDiscrepancies.some((p) =>
                  pendingKeys.has(discrepancyKey(p)),
                )}
                className="text-xs font-medium disabled:opacity-50"
                style={{ color: 'var(--color-needs-human-text)' }}
                data-testid="file-all-discrepancies"
              >
                File all as issues
              </button>
            )}
          </div>
          <div className="space-y-2">
            {discrepancies.map((p, idx) => {
              const key = discrepancyKey(p);
              return (
                <DiscrepancyCard
                  key={`${p.check_type}:${idx}`}
                  proposal={p}
                  filed={filedKeys.has(key)}
                  pending={pendingKeys.has(key)}
                  onFile={() => fileProposals([p], [key])}
                />
              );
            })}
          </div>
        </section>
      )}

      {/* ---- Transaction categorization (propose → apply) ---- */}
      {transactions.length > 0 && (
        <section data-testid="proposal-group-transactions">
          <div className="flex items-center gap-1.5 mb-2">
            <Tags className="w-3.5 h-3.5" style={{ color: 'var(--color-needs-human-text)' }} />
            <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-label)' }}>
              Uncategorized transactions
            </h4>
            <span className="text-xs" style={{ color: 'var(--color-text-dim)' }}>
              ({transactions.length})
            </span>
          </div>
          <div className="space-y-2">
            {transactions.map((p, idx) => {
              const key = transactionKey(p);
              return (
                <TransactionProposalCard
                  key={`${p.transaction_id}:${idx}`}
                  proposal={p}
                  reportId={reportId}
                  applied={appliedKeys.has(key)}
                  applying={applyingKeys.has(key)}
                  onApplying={(on) => markApplying(key, on)}
                  onApplied={() => markApplied(key)}
                />
              );
            })}
          </div>
        </section>
      )}

      {/* ---- Anomaly triage (propose → apply) ---- */}
      {anomalies.length > 0 && (
        <section data-testid="proposal-group-anomalies">
          <div className="flex items-center gap-1.5 mb-2">
            <ShieldAlert className="w-3.5 h-3.5" style={{ color: 'var(--color-needs-human-text)' }} />
            <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-label)' }}>
              Anomalies to triage
            </h4>
            <span className="text-xs" style={{ color: 'var(--color-text-dim)' }}>({anomalies.length})</span>
          </div>
          <div className="space-y-2">
            {anomalies.map((p, idx) => {
              const key = anomalyKey(p);
              return (
                <AnomalyProposalCard
                  key={`${p.anomaly_id}:${idx}`}
                  proposal={p}
                  reportId={reportId}
                  resolved={appliedKeys.has(key)}
                  resolving={applyingKeys.has(key)}
                  onResolving={(on) => markApplying(key, on)}
                  onResolved={() => markApplied(key)}
                />
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

function OverrideProposalCard({
  proposal,
  onReview,
}: {
  proposal: OverrideProposal;
  onReview: () => void;
}) {
  const current = formatProposalValue(proposal.current_value, '—');
  const suggested = formatProposalValue(proposal.suggested_value, 'value to enter');
  return (
    <div
      className="rounded-lg p-3"
      style={{ border: '1px solid var(--color-needs-human-tint-bd)', backgroundColor: 'var(--color-needs-human-tint)' }}
      data-testid="override-proposal-card"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate" style={{ color: 'var(--color-text-primary)' }}>
            {proposal.field_label}
          </p>
          <p className="text-[11px] truncate" style={{ color: 'var(--color-text-muted)' }}>
            {proposal.engine_id}
          </p>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2 text-xs">
        <span
          className="font-mono tabular-nums"
          style={{ color: 'var(--color-text-secondary)' }}
          title="Current value"
        >
          {current.text}
        </span>
        <ArrowRight className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--color-text-dim)' }} />
        <span
          className={`font-mono tabular-nums ${
            suggested.isPlaceholder ? 'italic' : 'font-semibold'
          }`}
          style={{ color: suggested.isPlaceholder ? 'var(--color-text-dim)' : 'var(--color-needs-human-text)' }}
          title="Suggested value"
        >
          {suggested.text}
        </span>
      </div>
      {proposal.rationale && (
        <p className="mt-2 text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
          {proposal.rationale}
        </p>
      )}
      <div className="mt-2.5 flex justify-end">
        <button
          onClick={onReview}
          className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors"
          style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          data-testid="override-proposal-review"
        >
          <SlidersHorizontal className="w-3 h-3" />
          Review &amp; apply
        </button>
      </div>
    </div>
  );
}

function FiledOrFileButton({
  filed,
  pending,
  onFile,
  testId,
}: {
  filed: boolean;
  pending: boolean;
  onFile: () => void;
  testId: string;
}) {
  if (filed) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium"
        style={{ backgroundColor: 'transparent', color: 'var(--color-verified)' }}
        data-testid={`${testId}-filed`}
      >
        <Check className="w-3 h-3" />
        Filed
      </span>
    );
  }
  return (
    <button
      onClick={onFile}
      disabled={pending}
      className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium hover:bg-[var(--color-bg-hover)] disabled:opacity-50"
      style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-text-secondary)' }}
      data-testid={testId}
    >
      {pending ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
      File as issue
    </button>
  );
}

function MissingDocCard({
  proposal,
  filed,
  pending,
  onFile,
}: {
  proposal: MissingDocumentProposal;
  filed: boolean;
  pending: boolean;
  onFile: () => void;
}) {
  return (
    <div
      className="rounded-lg p-3"
      style={{ border: '1px solid var(--color-needs-human-tint-bd)', backgroundColor: 'var(--color-needs-human-tint)' }}
      data-testid="missing-doc-card"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          {proposal.display_name}
        </p>
        {proposal.priority && (
          <span
            className="flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide"
            style={badgeStyle(PRIORITY_BADGE, proposal.priority)}
          >
            {proposal.priority}
          </span>
        )}
      </div>
      {proposal.rationale && (
        <p className="mt-1.5 text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
          {proposal.rationale}
        </p>
      )}
      <div className="mt-2.5 flex justify-end">
        <FiledOrFileButton
          filed={filed}
          pending={pending}
          onFile={onFile}
          testId="missing-doc-file"
        />
      </div>
    </div>
  );
}

function DiscrepancyCard({
  proposal,
  filed,
  pending,
  onFile,
}: {
  proposal: DiscrepancyProposal;
  filed: boolean;
  pending: boolean;
  onFile: () => void;
}) {
  return (
    <div
      className="rounded-lg p-3"
      style={{ border: '1px solid var(--color-needs-human-tint-bd)', backgroundColor: 'var(--color-needs-human-tint)' }}
      data-testid="discrepancy-card"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium leading-snug" style={{ color: 'var(--color-text-primary)' }}>
          {proposal.message}
        </p>
        {proposal.severity && (
          <span
            className="flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide"
            style={badgeStyle(SEVERITY_BADGE, proposal.severity)}
          >
            {proposal.severity}
          </span>
        )}
      </div>
      {proposal.recommendation && (
        <p className="mt-1.5 text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
          <span className="font-medium" style={{ color: 'var(--color-text-secondary)' }}>Recommendation: </span>
          {proposal.recommendation}
        </p>
      )}
      {proposal.source_documents && proposal.source_documents.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {proposal.source_documents.map((doc, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px]"
              style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
            >
              <FileText className="w-2.5 h-2.5" />
              {doc}
            </span>
          ))}
        </div>
      )}
      <div className="mt-2.5 flex justify-end">
        <FiledOrFileButton
          filed={filed}
          pending={pending}
          onFile={onFile}
          testId="discrepancy-file"
        />
      </div>
    </div>
  );
}

/**
 * Propose → apply card for an AI-suggested transaction categorization.
 * The accountant must type a >=10-char `reason` (the audit attestation — never
 * prefilled with AI text) before applying. If the AI withheld a
 * `suggested_category`, the accountant must also type the category to apply.
 */
function TransactionProposalCard({
  proposal,
  reportId,
  applied,
  applying,
  onApplying,
  onApplied,
}: {
  proposal: TransactionProposal;
  reportId: string;
  applied: boolean;
  applying: boolean;
  onApplying: (on: boolean) => void;
  onApplied: () => void;
}) {
  const [reason, setReason] = useState('');
  // When the AI withheld a category, the accountant supplies one here.
  const [categoryInput, setCategoryInput] = useState('');

  const hasSuggestion =
    proposal.suggested_category !== null &&
    proposal.suggested_category !== undefined &&
    proposal.suggested_category !== '';
  const categoryToApply = hasSuggestion
    ? (proposal.suggested_category as string)
    : categoryInput.trim();

  const amount = formatTxnAmount(proposal.amount);
  const reasonOk = reason.trim().length >= 10;
  const categoryOk = categoryToApply.length > 0;
  const canApply = !applied && !applying && reasonOk && categoryOk;

  const apply = async () => {
    if (!canApply) return;
    onApplying(true);
    try {
      await intelligenceApi.reclassifyTransaction(
        reportId,
        proposal.transaction_id,
        categoryToApply,
        reason.trim(),
      );
      onApplied();
      toast.success('Categorized — re-run engines to refresh');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to apply category');
    } finally {
      onApplying(false);
    }
  };

  return (
    <div
      className="rounded-lg p-3"
      style={{ border: '1px solid var(--color-needs-human-tint-bd)', backgroundColor: 'var(--color-needs-human-tint)' }}
      data-testid="transaction-proposal-card"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium leading-snug" style={{ color: 'var(--color-text-primary)' }}>
            {proposal.description}
          </p>
          {proposal.date && (
            <p className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>{proposal.date}</p>
          )}
        </div>
        <span
          className="flex-shrink-0 font-mono text-xs tabular-nums"
          style={{ color: amount.isNegative ? 'var(--color-text-muted)' : 'var(--color-text-secondary)' }}
          title="Transaction amount"
        >
          {amount.text}
        </span>
      </div>

      <div className="mt-2 flex items-center gap-2 text-xs">
        <span className="font-mono" style={{ color: 'var(--color-text-secondary)' }} title="Current category">
          {proposal.current_category || 'Uncategorized'}
        </span>
        <ArrowRight className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--color-text-dim)' }} />
        {hasSuggestion ? (
          <span
            className="font-mono font-semibold"
            style={{ color: 'var(--color-needs-human-text)' }}
            title="Suggested category"
          >
            {proposal.suggested_category}
          </span>
        ) : (
          <input
            type="text"
            value={categoryInput}
            onChange={(e) => setCategoryInput(e.target.value)}
            disabled={applied}
            placeholder="category to apply"
            className="flex-1 min-w-0 rounded-md px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
            style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
            data-testid="transaction-category-input"
          />
        )}
      </div>

      {proposal.reasoning && (
        <p className="mt-2 text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
          {proposal.reasoning}
        </p>
      )}

      <div className="mt-2.5">
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={applied}
          placeholder="Reason for this categorization (min 10 chars)"
          className="w-full rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
          style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
          data-testid="transaction-reason-input"
        />
      </div>

      <div className="mt-2.5 flex justify-end">
        {applied ? (
          <span
            className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium"
            style={{ backgroundColor: 'transparent', color: 'var(--color-verified)' }}
            data-testid="transaction-apply-applied"
          >
            <Check className="w-3 h-3" />
            Applied ✓
          </span>
        ) : (
          <button
            onClick={apply}
            disabled={!canApply}
            className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            data-testid="transaction-apply"
          >
            {applying ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            Apply category
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Propose → apply card for an AI-flagged anomaly. The accountant can add an
 * optional note and approve the resolution; on success the card settles to
 * "Resolved ✓".
 */
function AnomalyProposalCard({
  proposal,
  reportId,
  resolved,
  resolving,
  onResolving,
  onResolved,
}: {
  proposal: AnomalyProposal;
  reportId: string;
  resolved: boolean;
  resolving: boolean;
  onResolving: (on: boolean) => void;
  onResolved: () => void;
}) {
  const [note, setNote] = useState('');

  const approve = async () => {
    if (resolved || resolving) return;
    onResolving(true);
    try {
      await intelligenceApi.resolveAnomaly(
        reportId,
        proposal.anomaly_id,
        'approve_resolution',
        note.trim(),
      );
      onResolved();
      toast.success('Anomaly cleared');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to resolve anomaly');
    } finally {
      onResolving(false);
    }
  };

  return (
    <div
      className="rounded-lg p-3"
      style={{ border: '1px solid var(--color-needs-human-tint-bd)', backgroundColor: 'var(--color-needs-human-tint)' }}
      data-testid="anomaly-proposal-card"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium leading-snug" style={{ color: 'var(--color-text-primary)' }}>
          {proposal.title}
        </p>
        {proposal.severity && (
          <span
            className="flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide"
            style={badgeStyle(SEVERITY_BADGE, proposal.severity)}
          >
            {proposal.severity}
          </span>
        )}
      </div>
      {proposal.explanation && (
        <p className="mt-1.5 text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
          {proposal.explanation}
        </p>
      )}
      {proposal.recommendation && (
        <p className="mt-1.5 text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
          <span className="font-medium" style={{ color: 'var(--color-text-secondary)' }}>Recommendation: </span>
          {proposal.recommendation}
        </p>
      )}

      <div className="mt-2.5">
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={resolved}
          placeholder="Note (optional)"
          className="w-full rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
          style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
          data-testid="anomaly-note-input"
        />
      </div>

      <div className="mt-2.5 flex justify-end">
        {resolved ? (
          <span
            className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium"
            style={{ backgroundColor: 'transparent', color: 'var(--color-verified)' }}
            data-testid="anomaly-approve-resolved"
          >
            <Check className="w-3 h-3" />
            Resolved ✓
          </span>
        ) : (
          <button
            onClick={approve}
            disabled={resolving}
            className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            data-testid="anomaly-approve"
          >
            {resolving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            Approve resolution
          </button>
        )}
      </div>
    </div>
  );
}

export default function CaseIntelligenceChat({
  reportId,
  reportName,
  isOpen,
  onClose,
  toolScope,
}: CaseIntelligenceChatProps) {
  const navigate = useNavigate();
  const isAccountantScope = toolScope === 'accountant';
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [suggestedFollowups, setSuggestedFollowups] = useState<string[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Send message handler
  const sendMessage = useCallback(async (messageText?: string) => {
    const text = messageText || input.trim();
    if (!text || isLoading) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setSuggestedFollowups([]);

    try {
      const response: ChatResponse = await intelligenceApi.sendMessage(
        reportId,
        text,
        conversationId || undefined,
        false,
        toolScope
      );

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response.response,
        timestamp: new Date(),
        tool_calls: response.tool_calls,
        citations: response.citations
      };

      setMessages(prev => [...prev, assistantMessage]);
      setConversationId(response.conversation_id);
      setSuggestedFollowups(response.suggested_followups || []);

    } catch (error: any) {
      toast.error(error.message || 'Failed to send message');
      // Remove the user message on error
      setMessages(prev => prev.filter(m => m.id !== userMessage.id));
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, reportId, conversationId, toolScope]);

  // Handle keyboard input
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Copy message to clipboard
  const copyMessage = (content: string, id: string) => {
    navigator.clipboard.writeText(content);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Render citations
  const renderCitations = (citations: Citation[]) => {
    if (!citations || citations.length === 0) return null;
    
    return (
      <div className="mt-2 pt-2" style={{ borderTop: '1px solid var(--color-border-sub)' }}>
        <p className="text-xs mb-1" style={{ color: 'var(--color-text-muted)' }}>Sources:</p>
        <div className="flex flex-wrap gap-1">
          {citations.map((citation, idx) => (
            <span
              key={idx}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full"
              style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
            >
              <FileText className="w-3 h-3" />
              {citation.source_name}
            </span>
          ))}
        </div>
      </div>
    );
  };

  // Render tool calls
  const renderToolCalls = (toolCalls: ToolCall[]) => {
    if (!toolCalls || toolCalls.length === 0) return null;

    return (
      <div className="mt-2 flex flex-wrap gap-1">
        {toolCalls.map((tc, idx) => {
          const Icon = TOOL_ICONS[tc.tool_name] || Calculator;
          const name = TOOL_NAMES[tc.tool_name] || tc.tool_name;
          
          return (
            <span
              key={idx}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full"
              style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
              title={`Parameters: ${JSON.stringify(tc.parameters)}`}
            >
              <Icon className="w-3 h-3" />
              {name}
            </span>
          );
        })}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed bottom-4 right-4 w-[420px] h-[600px] rounded-xl shadow-2xl flex flex-col z-50"
      style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
      data-testid="intelligence-chat"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 rounded-t-xl" style={{ borderBottom: '1px solid var(--color-border)', backgroundColor: 'var(--color-brand)' }}>
        <div className="flex items-center gap-2 text-white">
          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">
              {isAccountantScope ? 'Forensic Assistant' : 'Case Intelligence'}
            </h3>
            <p className="text-xs text-white/70 truncate max-w-[200px]">
              {isAccountantScope
                ? 'Calculation & forensic scope'
                : reportName || reportId}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
          data-testid="close-chat"
        >
          <X className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
              <Bot className="w-8 h-8" style={{ color: 'var(--color-needs-human-text)' }} />
            </div>
            <h4 className="font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
              {isAccountantScope ? 'Ask about these numbers' : 'Ask me anything about this case'}
            </h4>
            <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
              {isAccountantScope
                ? 'I can stress-test the claim, run calculations, and explain the forensic accounting. Litigation strategy is out of scope here.'
                : 'I can analyze data, run calculations, and provide different perspectives.'}
            </p>
            <div className="space-y-2 w-full">
              {(isAccountantScope
                ? [
                    "How robust is this claim number?",
                    "Where is the claim most sensitive?",
                    "Run a Monte Carlo simulation",
                  ]
                : [
                    "What's the summary of this case?",
                    "Show me the settlement range",
                    "What would the adjuster challenge?",
                  ]
              ).map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => sendMessage(suggestion)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--color-bg-hover)] rounded-lg transition-colors"
                  style={{ backgroundColor: 'var(--color-bg-base)', color: 'var(--color-text-secondary)' }}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
                data-testid={`message-${message.role}`}
              >
                {/* Avatar */}
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'var(--color-needs-human-tint)' }}
                >
                  {message.role === 'user'
                    ? <User className="w-4 h-4" style={{ color: 'var(--color-needs-human-text)' }} />
                    : <Bot className="w-4 h-4" style={{ color: 'var(--color-needs-human-text)' }} />
                  }
                </div>

                {/* Message content */}
                <div className={`flex-1 max-w-[85%] ${message.role === 'user' ? 'text-right' : ''}`}>
                  <div
                    className="inline-block px-4 py-2 rounded-xl"
                    style={
                      message.role === 'user'
                        ? { backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                        : { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }
                    }
                  >
                    {message.role === 'assistant' ? (
                      <div className="prose prose-sm max-w-none">
                        <div 
                          className="text-sm whitespace-pre-wrap"
                          dangerouslySetInnerHTML={{ 
                            __html: message.content
                              .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                              .replace(/\n## (.*?)\n/g, '<h3 class="font-semibold text-base mt-3 mb-1">$1</h3>')
                              .replace(/\n- /g, '<br/>• ')
                          }}
                        />
                      </div>
                    ) : (
                      <p className="text-sm">{message.content}</p>
                    )}
                  </div>

                  {/* Tool calls and citations for assistant messages */}
                  {message.role === 'assistant' && (
                    <>
                      {renderToolCalls(message.tool_calls || [])}
                      {(() => {
                        // Surface a "prep the claim" tool result as grouped
                        // proposal cards (override / missing-doc / discrepancy).
                        const prep = (message.tool_calls || []).find(
                          (tc) => tc.tool_name === 'prepare_claim',
                        );
                        if (!prep || !prep.result) return null;
                        return (
                          <ProposalCards
                            result={prep.result as unknown as PrepareClaimResult}
                            reportId={reportId}
                            navigate={navigate}
                          />
                        );
                      })()}
                      {renderCitations(message.citations || [])}
                      
                      {/* Copy button */}
                      <button
                        onClick={() => copyMessage(message.content, message.id)}
                        className="mt-1 text-xs flex items-center gap-1"
                        style={{ color: 'var(--color-text-dim)' }}
                      >
                        {copiedId === message.id ? (
                          <>
                            <Check className="w-3 h-3" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3" />
                            Copy
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                  <Bot className="w-4 h-4" style={{ color: 'var(--color-needs-human-text)' }} />
                </div>
                <div className="rounded-xl px-4 py-3" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                  <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Analyzing...
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Suggested follow-ups */}
      {suggestedFollowups.length > 0 && !isLoading && (
        <div className="px-4 py-2" style={{ borderTop: '1px solid var(--color-border-sub)' }}>
          <p className="text-xs mb-2" style={{ color: 'var(--color-text-muted)' }}>Suggested follow-ups:</p>
          <div className="flex flex-wrap gap-1">
            {suggestedFollowups.map((followup, idx) => (
              <button
                key={idx}
                onClick={() => sendMessage(followup)}
                className="px-2 py-1 text-xs rounded-full transition-colors"
                style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
                data-testid={`followup-${idx}`}
              >
                {followup}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="p-3" style={{ borderTop: '1px solid var(--color-border)' }}>
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about this case..."
            className="flex-1 resize-none rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-brand)] focus:border-transparent max-h-24"
            style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
            rows={1}
            disabled={isLoading}
            data-testid="chat-input"
          />
          <button
            onClick={() => sendMessage()}
            disabled={!input.trim() || isLoading}
            className="p-2.5 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            data-testid="send-button"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Floating toggle button for opening chat
// Positioned above the existing AdjusterQABot button
export function IntelligenceChatButton({ 
  onClick, 
  hasUnread = false 
}: { 
  onClick: () => void;
  hasUnread?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className="fixed bottom-24 right-6 w-12 h-12 rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center z-50"
      style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
      data-testid="open-intelligence-chat"
      title="Case Intelligence AI"
    >
      <Sparkles className="w-5 h-5" />
      {hasUnread && (
        <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full border-2 border-white" style={{ backgroundColor: 'var(--color-brand)' }} />
      )}
    </button>
  );
}
